import {DOCUMENT_CATEGORIES, type DocumentCategory, type Matter} from './fenzsafeStore';
import {csrfToken} from './api';

/**
 * The ProFenz handoff contract (Priority 6 of the 2026-09-12 checkpoint instruction), implementing
 * "option 2: document-only handoff" from docs/ai/FENZSAFE_CODEX_INTEGRATION_PACKAGE.md §8.3 -- the
 * recommended default specifically to avoid FenzSafe turning into a second intake system that
 * duplicates what DocumentFirstIntake.tsx already collects (recipient name, service address,
 * court).
 *
 * What this hands off: the matter's caption and a reference to its current (non-superseded)
 * documents, via carolinaWorkflowStore.ts's own `NewCompanyFileInput.sourceOrderReference` field --
 * built for exactly this "came from another system, don't re-key it" case, so the matter's own
 * reference carries across without duplicate entry. `jobs`/`subject`/`serviceAddress`/`court` are
 * deliberately left for the attorney to fill in on the ProFenz side; see §8.3's real gap finding
 * (FenzSafe's own `Matter` type has no recipient/court/address fields to pre-fill them from at
 * all -- extending it to collect those would be a real product-scope expansion, not a wiring task).
 *
 * Wired into `AttorneyIntake.tsx` (Issue #114 founder-delegated re-review, comment
 * 5724759851/5725255188, Slice D) -- `DocumentFirstIntake.tsx`, which this contract originally
 * assumed was the real intake surface, turned out to be reachable only from the synthetic
 * walkthrough (`EndToEndWalkthrough.tsx`/`LocalWalkthrough.tsx`), never from `Application.tsx`'s
 * real routing. `AttorneyIntake.tsx` is the genuine authenticated documents-first flow; see its own
 * `fenzsafeHandoff` prop doc comment for exactly how this context is applied (additive-only, a
 * fresh never-saved draft only, no auto-submit).
 *
 * R9 correction (Issue #192 comment `5803848964`, approving the proposal in PR #202 comment
 * `5803712885`): the real production handoff (`FenzSafeShareView.tsx`'s "Start a ProFenz service
 * request" link -> `LoginApp.tsx`/`MfaApp.tsx` -> `Application.tsx`) no longer puts the context
 * itself in the URL. `createProfenzHandoffToken()`/`consumeProfenzHandoffToken()` exchange it for
 * an opaque, single-use, short-lived server-side token (`FenzSafePendingHandoffController` /
 * `Version202609230001`); only that meaningless token ever reaches browser history, logs, or a
 * referrer header. `encodeProfenzHandoffQuery()`/`decodeProfenzHandoffQuery()`/`profenzHandoffUrl()`
 * below remain exactly as they were, but are now used ONLY by `FenzSafeAttorneyView.tsx`'s
 * synthetic, fully local walkthrough demo (`FenzSafeWalkthrough.tsx`) -- it never calls the real
 * backend, so the privacy/tampering concerns this correction addresses don't apply to it, and
 * changing its self-contained demo format isn't part of this correction's scope.
 */
export type ProfenzHandoffDocumentReference = {
  name: string;
  category: DocumentCategory;
};

export type ProfenzHandoffContext = {
  sourceMatterId: string;
  caption: string;
  sourceOrderReference: string;
  documents: ProfenzHandoffDocumentReference[];
};

/** Only current (non-superseded) documents are referenced -- an attorney starting a service request cares about the matter's present state, not every historical version. */
export function buildProfenzHandoffContext(matter: Matter): ProfenzHandoffContext {
  return {
    sourceMatterId: matter.id,
    caption: matter.name,
    sourceOrderReference: matter.reference,
    documents: matter.documents.filter((doc) => !doc.supersededBy).map((doc) => ({name: doc.name, category: doc.category})),
  };
}

const HANDOFF_QUERY_PARAM = 'fenzsafeHandoff';

export function encodeProfenzHandoffQuery(context: ProfenzHandoffContext): URLSearchParams {
  const params = new URLSearchParams();
  params.set(HANDOFF_QUERY_PARAM, JSON.stringify(context));
  return params;
}

// Bounds independently reviewed as blocking (PR202 review 5297278216): the original decode only
// checked top-level types, so a syntactically valid but attacker-controlled payload (oversized
// strings, an unbounded documents array, or a document with an invalid/fabricated category) was
// accepted and canonically re-encoded onward. These caps match real matter sizes with headroom,
// not the exact current UI limits, so a genuine matter is never rejected.
const MAX_ENCODED_LENGTH = 4000;
const MAX_TEXT_FIELD_LENGTH = 500;
const MAX_DOCUMENT_NAME_LENGTH = 300;
const MAX_DOCUMENTS = 50;

function isBoundedString(value: unknown, maxLength: number): value is string {
  return typeof value === 'string' && value.length > 0 && value.length <= maxLength;
}

function isValidDocumentReference(value: unknown): value is ProfenzHandoffDocumentReference {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return isBoundedString(candidate.name, MAX_DOCUMENT_NAME_LENGTH)
    && typeof candidate.category === 'string'
    && (DOCUMENT_CATEGORIES as readonly string[]).includes(candidate.category);
}

/**
 * Shared by `decodeProfenzHandoffQuery()` (walkthrough) and `consumeProfenzHandoffToken()` (real
 * production path) -- every field is bounds- and shape-checked (see the caps above) and the
 * return value is rebuilt field-by-field from validated data only, so no unexpected/extra
 * property on the input can ever survive into the trusted `ProfenzHandoffContext` shape.
 */
function validateHandoffContextShape(candidate: unknown): ProfenzHandoffContext | null {
  if (typeof candidate !== 'object' || candidate === null) return null;
  const record = candidate as Record<string, unknown>;
  if (
    !isBoundedString(record.sourceMatterId, MAX_TEXT_FIELD_LENGTH)
    || !isBoundedString(record.caption, MAX_TEXT_FIELD_LENGTH)
    || !isBoundedString(record.sourceOrderReference, MAX_TEXT_FIELD_LENGTH)
  ) {
    return null;
  }
  if (!Array.isArray(record.documents) || record.documents.length > MAX_DOCUMENTS || !record.documents.every(isValidDocumentReference)) {
    return null;
  }
  const documents = record.documents as ProfenzHandoffDocumentReference[];
  return {
    sourceMatterId: record.sourceMatterId,
    caption: record.caption,
    sourceOrderReference: record.sourceOrderReference,
    documents: documents.map((doc) => ({name: doc.name, category: doc.category})),
  };
}

/**
 * Synthetic-walkthrough-only (see this module's own doc comment). Returns null for a missing,
 * malformed, oversized, or shape-invalid param rather than throwing -- a cold/direct visit to the
 * intake route (no FenzSafe handoff at all) must still work exactly as it does today.
 */
export function decodeProfenzHandoffQuery(params: URLSearchParams): ProfenzHandoffContext | null {
  const raw = params.get(HANDOFF_QUERY_PARAM);
  if (!raw || raw.length > MAX_ENCODED_LENGTH) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  return validateHandoffContextShape(parsed);
}

/**
 * Synthetic-walkthrough-only (see this module's own doc comment) -- the real production flow uses
 * `profenzHandoffUrlForToken()` below instead. `Application.tsx` renders `AttorneyIntake.tsx` for a
 * signed-in user with the `attorney_review` capability at this same root, either way.
 */
export function profenzHandoffUrl(matter: Matter): string {
  const query = encodeProfenzHandoffQuery(buildProfenzHandoffContext(matter));
  return `/?${query.toString()}`;
}

/**
 * R9's real production create step: exchanges a validated context for an opaque, single-use,
 * short-lived server-side token via the deliberately unauthenticated
 * `FenzSafePendingHandoffController::create()` (an anonymous share visitor has no ProFenz session
 * yet, same reasoning as every other unauthenticated FenzSafe share endpoint). Returns null on any
 * failure (network, validation, rate limit) rather than throwing -- callers should treat that as
 * "the handoff could not be started," not crash the share page.
 */
export async function createProfenzHandoffToken(context: ProfenzHandoffContext): Promise<string | null> {
  try {
    const response = await fetch('/api/v1/fenzsafe/handoff', {
      method: 'POST',
      credentials: 'omit',
      headers: {'Content-Type': 'application/json', Accept: 'application/json'},
      body: JSON.stringify({context}),
    });
    if (!response.ok) return null;
    const body = (await response.json()) as {handoff_token?: unknown};
    return typeof body.handoff_token === 'string' && body.handoff_token.length > 0 ? body.handoff_token : null;
  } catch {
    return null;
  }
}

/**
 * R9's real production consume step, called once by `Application.tsx` after a real sign-in
 * establishes a session (`FenzSafePendingHandoffController::consume()` requires authentication).
 * The response is independently re-validated with the same bounds `decodeProfenzHandoffQuery()`
 * enforces (never trust a network response's shape outright) and rebuilt field-by-field, so no
 * unexpected server-side property can survive into the trusted `ProfenzHandoffContext` shape.
 */
export async function consumeProfenzHandoffToken(token: string): Promise<ProfenzHandoffContext | null> {
  try {
    const response = await fetch('/api/v1/fenzsafe/handoff/consume', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {'Content-Type': 'application/json', Accept: 'application/json', 'X-ProFenz-CSRF': csrfToken()},
      body: JSON.stringify({handoff_token: token}),
    });
    if (!response.ok) return null;
    const body = (await response.json()) as {context?: unknown};
    return validateHandoffContextShape(body.context);
  } catch {
    return null;
  }
}

/** Builds the real production handoff URL: only the opaque token, never the payload itself. */
export function profenzHandoffUrlForToken(token: string): string {
  const params = new URLSearchParams();
  params.set(HANDOFF_QUERY_PARAM, token);
  return `/?${params.toString()}`;
}

/** The opaque token is short and URL-safe (see OneTimeToken::issue() server-side); this is a plausibility bound, not a cryptographic check -- the server is the sole source of truth on validity. */
const MAX_TOKEN_LENGTH = 200;

function isPlausibleHandoffToken(value: string | null): value is string {
  return typeof value === 'string' && value.length > 0 && value.length <= MAX_TOKEN_LENGTH;
}

/**
 * Independent review finding R9 (Issue #164/#192): an unauthenticated share visitor who follows
 * the share view's handoff link lands on `/` with the handoff still in the URL, but
 * `Application.tsx` renders three consecutive unauthenticated-only probe components
 * (PersonalSignupWorkspace -> OrganizationSelectionGate -> LoginApp) before a real sign-in form
 * appears -- none of which touch the query string, so it survives that chain untouched. The actual
 * loss happens where `LoginApp.tsx`/`MfaApp.tsx` navigate by *replacing* the URL (`window.location
 * .assign('/')`, or a plain `href="/"`) rather than continuing from the current one, which drops
 * any query string outright.
 *
 * This carries the opaque token across exactly those navigations. Since the R9 correction above,
 * the URL never carries anything but that meaningless, single-use, short-lived identifier, so
 * there is no payload left to re-validate here -- only a plausibility length check, matching the
 * server's own bound.
 */
export function preserveProfenzHandoffQuery(targetPath: string): string {
  const token = new URLSearchParams(window.location.search).get(HANDOFF_QUERY_PARAM);
  if (!isPlausibleHandoffToken(token)) return targetPath;
  const separator = targetPath.includes('?') ? '&' : '?';
  const params = new URLSearchParams();
  params.set(HANDOFF_QUERY_PARAM, token);
  return `${targetPath}${separator}${params.toString()}`;
}
